import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { useLocation, useNavigate } from "react-router-dom";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { useFetchSalesBoosterHistoryMutation } from "../../../features/tenant/salesBooster/salesBoosterApi";
import toast from "react-hot-toast";
import { formatter } from "../../../utils/numberFormatter";
import CustomExport from "../../../components/CustomExport/CustomExport";

const SalesBoosterHistory = () => {
  const [
    fetchSalesBoosterHistory,
    {
      data: salesBoosterData,
      isLoading: salesBoosterIsLoading,
      error: salesBoosterError,
    },
  ] = useFetchSalesBoosterHistoryMutation();

  const [
    fetchSalesBoosterHistoryCsv,
    {
      data: salesBoosterDataCsv,
      isLoading: salesBoosterIsLoadingCsv,
      error: salesBoosterErrorCsv,
    },
  ] = useFetchSalesBoosterHistoryMutation();

  const navigate = useNavigate();
  const location = useLocation();

  const [totalRows, setTotalRows] = useState(0);
  const [data, setData] = useState([]);

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [csvData, setCsvData] = useState(null);

  const initialRequest = {
    name: "",
    mobile: "",
  };
  const [request, setRequest] = useState(initialRequest);

  const clearFilters = () => {
    setRequest(initialRequest);
  };

  const fetchHistoryFunc = () => {
    const sb_id = location?.state?.id;
    if (!sb_id) {
      toast.error("Sales Booster ID is missing");
      return;
    }

    fetchSalesBoosterHistory({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      sb_id,
      limit,
      offset,
    });
  };

  const fetchHistoryCsvFunc = () => {
    const sb_id = location?.state?.id;
    if (!sb_id) return;

    fetchSalesBoosterHistoryCsv({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      sb_id,
      limit: 100000,
      offset: 0,
    });
  };

  useEffect(() => {
    fetchHistoryFunc();
  }, [limit, offset, location?.state?.id]);

  useEffect(() => {
    if (salesBoosterData && salesBoosterData.body) {
      setData(salesBoosterData.body.data || []);
      setTotalRows(salesBoosterData.body.total || 0);
    }

    if (salesBoosterError) {
      toast.error("Something went wrong", { id: "salesBoosterError" });
      console.error("Sales Booster history error: ", salesBoosterError);
    }
  }, [salesBoosterData, salesBoosterError]);

  useEffect(() => {
    if (salesBoosterDataCsv && salesBoosterDataCsv.body) {
      setCsvData(salesBoosterDataCsv.body.data || []);
    }

    if (salesBoosterErrorCsv) {
      toast.error("Something went wrong during export", { id: "salesBoosterErrorCsv" });
      console.error("Sales Booster CSV error: ", salesBoosterErrorCsv);
      setCsvData([]);
    }
  }, [salesBoosterDataCsv, salesBoosterErrorCsv]);

  // Client-side filtering on current page data
  const filteredData = data.filter((row) => {
    const mobileMatch = request.mobile
      ? row.app_user_mobile?.toLowerCase().includes(request.mobile.toLowerCase())
      : true;
    const nameMatch = request.name
      ? row.app_user_name?.toLowerCase().includes(request.name.toLowerCase())
      : true;
    return mobileMatch && nameMatch;
  });


  const columns = [
    {
      name: "SNo",
      cell: (row, idx) => offset + Number(idx + 1),
      sortable: true,
      width: "80px",
    },
    {
      name: "Mobile",
      selector: (row) => row.app_user_mobile || "N/A",
      sortable: true,
      width: "140px",
    },
    {
      name: "User ID",
      selector: (row) => row.app_user_id || "N/A",
      sortable: true,
      width: "120px",
    },
    {
      name: "Name",
      selector: (row) => row.app_user_name || "N/A",
      sortable: true,
      width: "200px",
    },
    {
      name: "User Value",
      selector: (row) => formatter.format(row.user_value || 0),
      sortable: true,
      width: "150px",
    },
    {
      name: "Trigger Value",
      selector: (row) => formatter.format(row.trigger_value || 0),
      sortable: true,
      width: "150px",
    },
    {
      name: "Offer Type",
      selector: (row) => row.offer || "N/A",
      sortable: true,
      width: "130px",
      cell: (row) => <span className="capitalize">{row.offer || "N/A"}</span>,
    },
    {
      name: "Reward Value",
      selector: (row) => row.value || "N/A",
      sortable: true,
      width: "130px",
    },
    {
      name: "Date",
      selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A"),
      sortable: true,
      width: "130px",
    },
    {
      name: "Trigger Status",
      cell: (row) => {
        const isClaimed = row.trigger_status == "2" || row.trigger_status == 2;
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${isClaimed ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
              }`}
          >
            {isClaimed ? "Claimed" : "Pending"}
          </span>
        );
      },
      sortable: true,
      width: "130px",
    },
  ];

  const handlePageChange = (pageNumber) => {
    const newOffset = (pageNumber - 1) * limit;
    setOffset(newOffset);
  };

  const handlePerRowsChange = (newPerPage) => {
    setLimit(newPerPage);
  };

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full">
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            Sales Booster History
            <LinksComponent />
          </div>
          <div className="p-3">
            {location?.state && (
              <div className="mb-2">
                <CustomExport
                  fetchData={fetchHistoryCsvFunc}
                  data={csvData}
                  defaultColumnExport={[
                    "app_user_mobile",
                    "app_user_name",
                    "user_value",
                    "trigger_value",
                    "offer",
                    "value",
                    "trigger_status",
                    "created_at",
                  ]}
                  filename={`${location?.state?.name || "Sales_Booster"}_History`}
                />
              </div>
            )}

            {location?.state && (
              <div className="border-b border-gray-300 bg-sky-50 rounded-t-md px-4 py-2">
                <div className="flex max-w-xl justify-between gap-4">
                  <div>
                    <span className="text-gray-400 text-sm block">Scheme Name :</span>
                    <span className="flex text-black text-sm font-semibold gap-1">
                      {location.state.name}
                    </span>
                  </div>

                  <div>
                    <span className="text-gray-400 text-sm block">Start Date:</span>
                    <span className="flex text-black text-sm font-semibold gap-1">
                      {location.state.start_date}
                    </span>
                  </div>

                  <div>
                    <span className="text-gray-400 text-sm block">End Date :</span>
                    <span className="flex text-black text-sm font-semibold gap-1">
                      {location.state.end_date}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="p-3 w-full bg-white shadow rounded-lg">
              <DataTableComponent
                keyField={(row) => row.id}
                columns={columns}
                data={filteredData}
                loading={salesBoosterIsLoading}
                totalRows={totalRows}
                handlePerRowsChange={handlePerRowsChange}
                handlePageChange={handlePageChange}
              >
                <div className="border-b pb-3 border-gray-300 bg-gray-50 rounded-t-lg">
                  <div className="flex space-x-2 justify-end">
                    <div className="w-52 h-10">
                      <div className="relative searchin">
                        <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                          Mobile
                        </label>
                        <input
                          type="search"
                          className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                          placeholder="Mobile..."
                          value={request.mobile}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setRequest((prevState) => ({
                              ...prevState,
                              mobile: newValue,
                            }));
                          }}
                        />
                      </div>
                    </div>
                    <div className="w-52 h-10">
                      <div className="relative searchin">
                        <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                          Name
                        </label>
                        <input
                          type="search"
                          className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                          placeholder="Name..."
                          value={request.name}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setRequest((prevState) => ({
                              ...prevState,
                              name: newValue,
                            }));
                          }}
                        />
                      </div>
                    </div>

                    <div className="flex flex-row gap-2 h-10 align-bottom items-end pt-5">
                      <button
                        className="btn btn-outline-primary p-2"
                        onClick={fetchHistoryFunc}
                      >
                        Search
                      </button>
                      <button
                        className="btn btn-outline-primary p-2"
                        onClick={clearFilters}
                      >
                        Clear Filter
                      </button>
                    </div>
                  </div>
                </div>
              </DataTableComponent>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SalesBoosterHistory;
